import { useEffect } from "react";

const PHONE_QUERY = "(max-width: 575.95px)";

function tag(table: HTMLTableElement) {
  // Viewers (sticky header) and anything marked data-no-stack stay as scrolling tables.
  if (table.closest("[data-no-stack]") || table.classList.contains("MuiTable-stickyHeader")) return;
  const headRow = table.tHead?.rows[0];
  if (!headRow) return;
  const labels: string[] = [];
  for (const th of Array.from(headRow.cells)) {
    // Merged headers cannot be mapped to cells; keep the normal scrolling table.
    if (th.colSpan > 1) return;
    labels.push((th.textContent || "").trim());
  }
  for (const body of Array.from(table.tBodies)) {
    for (const row of Array.from(body.rows)) {
      Array.from(row.cells).forEach((cell, i) => {
        if (cell.colSpan > 1) return;
        const label = labels[i] ?? "";
        if (cell.getAttribute("data-label") !== label) cell.setAttribute("data-label", label);
      });
    }
  }
  table.setAttribute("data-stack", "true");
}

function apply(on: boolean) {
  document.querySelectorAll<HTMLTableElement>("table.MuiTable-root").forEach((table) => {
    if (on) tag(table);
    else table.removeAttribute("data-stack");
  });
}

/**
 * On phones, turns plain MUI tables into stacked "cards" (one block per row with the column title beside each
 * value). Tables inside an element with `data-no-stack` (data viewers, wide grids) keep horizontal scrolling.
 */
export function useStackedTables() {
  useEffect(() => {
    const media = window.matchMedia(PHONE_QUERY);
    let frame = 0;
    const run = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => apply(media.matches));
    };
    const observer = new MutationObserver(run);
    observer.observe(document.body, { childList: true, subtree: true });
    media.addEventListener("change", run);
    run();
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
      media.removeEventListener("change", run);
    };
  }, []);
}
